import React, { useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { CalendarEvent } from '@/types/calendar'
import RecurringEventBadge from './RecurringEventBadge'

interface EventTypeConfig {
  label: string
  icon: React.ComponentType<any>
  color: string
  bgColor: string
  textColor: string
  borderColor: string
}

interface WeekViewProps {
  events: CalendarEvent[]
  currentDate: Date
  onEventClick: (event: CalendarEvent) => void
  onDateChange: (date: Date) => void
  eventTypeConfig: Record<string, EventTypeConfig>
}

/**
 * WeekView Component
 *
 * Displays a weekly calendar view with time-slot grid layout.
 * Shows events positioned in hourly time slots (8am-6pm) with all-day events in header.
 *
 * @param events - Array of calendar events to display
 * @param currentDate - Current date for week calculation
 * @param onEventClick - Callback when an event is clicked
 * @param onDateChange - Callback when week navigation changes date
 * @param eventTypeConfig - Configuration for event type styling
 */
export const WeekView: React.FC<WeekViewProps> = ({
  events,
  currentDate,
  onEventClick,
  onDateChange,
  eventTypeConfig
}) => {
  /**
   * Calculates the week range containing the given date
   * Week starts on Sunday (0)
   */
  const weekRange = useMemo(() => {
    const current = new Date(currentDate)
    const dayOfWeek = current.getDay() // 0 = Sunday

    // Calculate start of week (Sunday)
    const start = new Date(current)
    start.setDate(current.getDate() - dayOfWeek)
    start.setHours(0, 0, 0, 0)

    // Calculate end of week (Saturday)
    const end = new Date(start)
    end.setDate(start.getDate() + 6)
    end.setHours(23, 59, 59, 999)

    // Generate array of 7 days
    const days: Date[] = []
    for (let i = 0; i < 7; i++) {
      const day = new Date(start)
      day.setDate(start.getDate() + i)
      days.push(day)
    }

    return { start, end, days }
  }, [currentDate])

  /**
   * Filters events to current week range
   */
  const weekEvents = useMemo(() => {
    return events.filter(event => {
      const eventStart = new Date(event.startDate)
      const eventEnd = new Date(event.endDate)
      return eventStart <= weekRange.end && eventEnd >= weekRange.start
    })
  }, [events, weekRange])

  /**
   * Separates events into all-day and timed events per day
   */
  const eventsByDay = useMemo(() => {
    return weekRange.days.map(day => {
      const dayStart = new Date(day)
      dayStart.setHours(0, 0, 0, 0)
      const dayEnd = new Date(day)
      dayEnd.setHours(23, 59, 59, 999)

      const dayEvents = weekEvents.filter(event => {
        const eventStart = new Date(event.startDate)
        const eventEnd = new Date(event.endDate)
        return eventStart <= dayEnd && eventEnd >= dayStart
      })

      const allDayEvents = dayEvents.filter(e => e.allDay)
      const timedEvents = dayEvents.filter(e => !e.allDay)

      return { day, allDayEvents, timedEvents }
    })
  }, [weekRange.days, weekEvents])

  /**
   * Generates time slot labels (8am - 6pm)
   */
  const timeSlots = useMemo(() => {
    const slots: { hour: number; label: string }[] = []
    for (let hour = 8; hour <= 18; hour++) {
      const period = hour >= 12 ? 'PM' : 'AM'
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
      slots.push({ hour, label: `${displayHour}:00 ${period}` })
    }
    return slots
  }, [])

  /**
   * Calculates event position within time grid
   * Returns CSS styles for absolute positioning
   */
  const calculateEventPosition = (event: CalendarEvent, dayDate: Date): React.CSSProperties => {
    const startTime = new Date(event.startDate)
    const endTime = new Date(event.endDate)

    // Clip event to current day boundaries
    const dayStart = new Date(dayDate)
    dayStart.setHours(8, 0, 0, 0) // 8am start
    const dayEnd = new Date(dayDate)
    dayEnd.setHours(18, 0, 0, 0) // 6pm end

    const eventStartClamped = startTime < dayStart ? dayStart : startTime
    const eventEndClamped = endTime > dayEnd ? dayEnd : endTime

    const startHour = eventStartClamped.getHours()
    const startMinutes = eventStartClamped.getMinutes()
    const endHour = eventEndClamped.getHours()
    const endMinutes = eventEndClamped.getMinutes()

    // Calculate offset from 8am in hours
    const startOffset = startHour + startMinutes / 60 - 8
    const endOffset = endHour + endMinutes / 60 - 8
    const duration = endOffset - startOffset

    const hourSlotHeight = 64 // pixels per hour
    const top = startOffset * hourSlotHeight
    const height = Math.max(duration * hourSlotHeight, 40) // Minimum 40px height

    return {
      position: 'absolute',
      top: `${top}px`,
      height: `${height}px`,
      left: '4px',
      right: '4px',
      zIndex: 10
    }
  }

  /**
   * Formats time for event display (e.g., "2:00 PM")
   */
  const formatEventTime = (dateString: string): string => {
    const date = new Date(dateString)
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const period = hours >= 12 ? 'PM' : 'AM'
    const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours
    const displayMinutes = minutes.toString().padStart(2, '0')
    return `${displayHour}:${displayMinutes} ${period}`
  }

  // Week navigation handlers
  const navigatePrevWeek = () => {
    const newDate = new Date(currentDate)
    newDate.setDate(currentDate.getDate() - 7)
    onDateChange(newDate)
  }

  const navigateNextWeek = () => {
    const newDate = new Date(currentDate)
    newDate.setDate(currentDate.getDate() + 7)
    onDateChange(newDate)
  }

  const goToToday = () => {
    onDateChange(new Date())
  }

  // Format week range label (e.g., "15-21 Out 2025")
  const weekRangeLabel = useMemo(() => {
    const startDay = weekRange.start.getDate()
    const endDay = weekRange.end.getDate()
    const month = weekRange.start.toLocaleDateString('pt-BR', { month: 'short' })
    const year = weekRange.start.getFullYear()
    return `${startDay}-${endDay} ${month} ${year}`
  }, [weekRange])

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return (
    <div>
      {/* Week Navigation Header */}
      <div className="p-6 border-b border-brand-gray/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={navigatePrevWeek}
              className="p-2 rounded-monumenta bg-brand-dark-lighter hover:bg-brand-primary hover:text-brand-dark transition-all duration-200"
              aria-label="Previous week"
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="font-display text-xl font-semibold text-white">
              Semana de {weekRangeLabel}
            </h2>
            <button
              onClick={navigateNextWeek}
              className="p-2 rounded-monumenta bg-brand-dark-lighter hover:bg-brand-primary hover:text-brand-dark transition-all duration-200"
              aria-label="Next week"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <button
            onClick={goToToday}
            className="btn-ghost text-sm px-3 py-1.5"
          >
            Hoje
          </button>
        </div>
      </div>

      {/* Week Grid */}
      <div className="p-6">
        <div className="flex" role="grid">
          {/* Time labels column */}
          <div className="w-20 flex-shrink-0">
            {/* Empty space for day headers */}
            <div className="h-24 border-b border-brand-gray/10"></div>

            {/* Time slot labels */}
            {timeSlots.map(slot => (
              <div
                key={slot.hour}
                className="h-16 flex items-start justify-end pr-3 text-xs text-brand-gray border-t border-brand-gray/10 first:border-t-0"
              >
                {slot.label}
              </div>
            ))}
          </div>

          {/* Day columns */}
          <div className="flex-1 grid grid-cols-7">
            {eventsByDay.map(({ day, allDayEvents, timedEvents }, dayIndex) => {
              const isToday = day.toDateString() === today.toDateString()
              const dayName = day.toLocaleDateString('pt-BR', { weekday: 'short' })
              const dayDate = day.getDate()

              return (
                <div
                  key={dayIndex}
                  className={`border-l border-brand-gray/10 first:border-l-0 ${
                    isToday ? 'bg-brand-primary/5' : ''
                  }`}
                >
                  {/* Day header with all-day events */}
                  <div className={`h-24 p-2 border-b border-brand-gray/10 ${
                    isToday ? 'bg-brand-primary/10' : ''
                  }`}>
                    <div className="text-center mb-2">
                      <div className={`text-xs font-medium ${
                        isToday ? 'text-brand-primary' : 'text-brand-gray'
                      }`}>
                        {dayName}
                      </div>
                      <div className={`text-sm font-bold ${
                        isToday ? 'text-brand-primary' : 'text-white'
                      }`}>
                        {dayDate}
                      </div>
                    </div>

                    {/* All-day events */}
                    <div className="space-y-1">
                      {allDayEvents.slice(0, 2).map(event => {
                        const config = eventTypeConfig[event.type]
                        const Icon = config.icon
                        const isRecurring = event.rrule || event.isRecurring

                        return (
                          <div
                            key={event.eventId}
                            onClick={() => onEventClick(event)}
                            className={`text-xs p-1 rounded cursor-pointer ${config.bgColor} ${config.textColor} hover:opacity-80 transition-opacity`}
                            title={event.title}
                          >
                            <div className="flex items-center gap-1 truncate">
                              <Icon size={8} />
                              <span className="truncate text-[10px]">{event.employeeName.split(' ')[0]}</span>
                              {isRecurring && <RecurringEventBadge size="sm" />}
                            </div>
                          </div>
                        )
                      })}
                      {allDayEvents.length > 2 && (
                        <div className="text-[9px] text-brand-gray text-center">
                          +{allDayEvents.length - 2}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Time grid for timed events */}
                  <div className="relative">
                    {timeSlots.map(slot => (
                      <div
                        key={slot.hour}
                        className="h-16 border-t border-brand-gray/10 first:border-t-0"
                      />
                    ))}

                    {/* Positioned timed events */}
                    {timedEvents.map(event => {
                      const config = eventTypeConfig[event.type]
                      const Icon = config.icon
                      const style = calculateEventPosition(event, day)
                      const isRecurring = event.rrule || event.isRecurring

                      return (
                        <div
                          key={event.eventId}
                          style={style}
                          onClick={() => onEventClick(event)}
                          className={`rounded cursor-pointer p-2 ${config.bgColor} ${config.textColor} hover:opacity-90 transition-opacity overflow-hidden`}
                          title={`${event.title} - ${event.description || ''}`}
                        >
                          <div className="flex items-center justify-between gap-1 mb-1">
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                              <Icon size={12} />
                              <span className="text-xs font-semibold truncate">
                                {event.employeeName.split(' ')[0]}
                              </span>
                            </div>
                            {isRecurring && <RecurringEventBadge size="sm" />}
                          </div>
                          <div className="text-[10px] font-medium">
                            {formatEventTime(event.startDate)}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default WeekView
